import type {ReactNode} from "react"

import {localize} from "@agenta/i18n"

import type {SidebarConfig} from "./types"

/** Where the support entries point. Static, so both shells carry the same destinations. */
export const SUPPORT_URLS = {
    docs: "https://agenta.ai/docs/",
    github: "https://github.com/Agenta-AI/agenta/issues",
    slack: "https://join.slack.com/t/agenta-hq/shared_invite/zt-37pnbp5s6-mbBrPL863d_oLB61GSNFjw",
} as const

export type SupportLinkKey = keyof typeof SUPPORT_URLS

/** Icons stay with the shell — each app draws from its own icon set. */
export type SupportLinkIcons = Partial<Record<SupportLinkKey | "help", ReactNode>>

export interface HelpDocsItemOptions {
    icons?: SupportLinkIcons
    /** Appended after the destinations (the Live Chat toggle, keyboard shortcuts, releases). */
    extraItems?: SidebarConfig[]
    /** Right-aligned content on the row (desktop's app version); hidden when the rail collapses. */
    suffix?: ReactNode
}

/**
 * The "Help & Docs" entry, shared by the desktop rail and the mobile drawer: the same
 * destinations in the same order. Host-specific rows (Live Chat, which needs Crisp; the
 * shortcuts sheet, which the host mounts) arrive through `extraItems` rather than being
 * reproduced per app.
 */
export const buildHelpDocsNavItem = ({
    icons = {},
    extraItems = [],
    suffix,
}: HelpDocsItemOptions = {}): SidebarConfig => ({
    key: "help-docs-link",
    title: localize("com_nav_help_docs"),
    icon: icons.help,
    suffix,
    submenu: [
        {
            key: "docs",
            title: localize("com_nav_documentation"),
            link: SUPPORT_URLS.docs,
            icon: icons.docs,
        },
        {
            key: "github-support",
            title: localize("com_nav_github_support"),
            link: SUPPORT_URLS.github,
            icon: icons.github,
        },
        {
            key: "slack-connect",
            title: localize("com_nav_slack_support"),
            link: SUPPORT_URLS.slack,
            icon: icons.slack,
        },
        {
            // MIT attribution for the upstream project and third-party packages.
            key: "licenses",
            title: localize("com_licenses_title"),
            link: "/licenses",
        },
        ...extraItems,
    ],
})

export interface InviteTeammateItemOptions {
    /** Project URL base; the item deep-links into the workspace tab with the modal open. */
    projectURL: string
    icon?: ReactNode
    /** Hidden when the viewer cannot invite (no session, no org, or no permission). */
    isHidden?: boolean
}

/**
 * The "Invite Teammate" entry, shared by the desktop rail and the mobile drawer.
 *
 * It lives here rather than in either app because the destination is the same on both: the
 * workspace settings tab with the invite modal open. The mobile drawer omitted it while that
 * tab did not exist there yet; it does now.
 */
export const buildInviteTeammateNavItem = ({
    projectURL,
    icon,
    isHidden,
}: InviteTeammateItemOptions): SidebarConfig => ({
    key: "invite-teammate-link",
    title: localize("com_nav_invite_teammate"),
    link: `${projectURL}/settings?tab=workspace&inviteModal=open`,
    icon,
    tooltip: localize("com_nav_invite_teammate"),
    isHidden,
    disabled: !projectURL,
})
